/**
 * Open a task by its key ("APB-42") — the task chips on commit rows (commit ↔
 * task links, slice 1). The board is found by its stored prefix among every
 * board the user can see (or one of its old prefixes), the task by its number (stored or planned), then
 * it opens like a task link (utils/boardLinks.openBoardLink).
 */
import { listBoardSummaries, readBoard } from '../../extensions/bundled/task-manager/boardsStore';
import { parseTaskKey } from '../../extensions/bundled/task-manager/taskKeys';
import { allPrefixesOf } from '../../extensions/bundled/task-manager/taskKeyPrefixEdit';
import { taskByNumber } from '../../extensions/bundled/task-manager/taskNumbers';
import { openBoardLink } from '../../utils/boardLinks';

export const PREFIX_CACHE_MS = 60 * 1000;
let prefixCache = null; // { at, promise }

/** The prefixes of every board the user can see (cached for a minute). */
export function knownTaskPrefixes({ refresh = false, now = Date.now } = {}) {
  if (!refresh && prefixCache && now() - prefixCache.at < PREFIX_CACHE_MS) return prefixCache.promise;
  const promise = listBoardSummaries()
    .then((list) => new Set((list || []).flatMap((s) => allPrefixesOf(s))))
    .catch(() => new Set());
  prefixCache = { at: now(), promise };
  return promise;
}

export function clearTaskPrefixCache() {
  prefixCache = null;
}

/** The board + task a key names, or null. */
export async function findTaskByKey(key) {
  const parsed = parseTaskKey(key);
  if (!parsed) return null;
  const summaries = (await listBoardSummaries()) || [];
  for (const s of summaries) {
    // The current prefix or an old one (a renamed board's keys keep resolving).
    if (!allPrefixesOf(s).includes(parsed.prefix)) continue;
    // eslint-disable-next-line no-await-in-loop
    const board = await readBoard(s.id, s.scopeKey).catch(() => null);
    const task = board ? taskByNumber(board, parsed.number) : null;
    if (task) return { board, task };
  }
  return null;
}

/** Opens the task; false when no board/task carries that key. */
export async function openTaskByKey(key) {
  const found = await findTaskByKey(key);
  if (!found) return false;
  await openBoardLink({ boardId: found.board.id, taskId: found.task.id });
  return true;
}
